import { CodigoSefazEnum } from '../../../../../shared/enums/financas/codigo-sefaz.enum';
import { TipoDestinoEnum } from '../../../../../shared/enums/financas/tipo-destino.enum';

/**
 * Como o recebimento se conecta ao dinheiro (§8.3):
 *   nenhuma ......... Dinheiro, Cheque, Crediário, formas cadastradas
 *   cartao .......... Cartão de crédito/débito — sempre por intermediador
 *   meio ............ Pix e Boleto — o usuário escolhe Banco (conta) ou Intermediador
 */
export type Intermediacao = 'nenhuma' | 'cartao' | 'meio';

/**
 * Como a conta de recebimento aparece no bloco (§8.3, tabela "Conta de recebimento"):
 *   nenhuma ................ Crediário (não gera financeiro direto)
 *   leituraIntermediador ... a conta do intermediador escolhido
 *   leituraBanco ........... a conta bancária informada acima (Depósito; Pix/Boleto via banco)
 *   contaCaixa ............. forma com destino Caixa: o campo é "Conta caixa"
 *   editavel ............... pré-preenchida com a conta do cadastro da forma
 *   obrigatoria ............ forma com destino Contas a Receber: vazia, o usuário escolhe
 */
export type ContaDeRecebimento =
    | 'nenhuma'
    | 'leituraIntermediador'
    | 'leituraBanco'
    | 'contaCaixa'
    | 'editavel'
    | 'obrigatoria';

/** O que a natureza da forma traz para o bloco do recebimento. */
export interface PerfilDaForma {
    /** Todas as formas têm categoria (§8.3). */
    categoria: boolean;
    /** Condição de pagamento existe só onde há parcelamento (§8.2). */
    condicao: boolean;
    /** Grade de parcelas e botão gerar/atualizar: só onde a condição é da EMPRESA (não do intermediador). */
    grade: boolean;
    intermediacao: Intermediacao;
    /** Bandeira só nos cartões, e DEPOIS do intermediador (§8.3). */
    bandeira: boolean;
    /** Depósito exige a conta bancária escolhida para salvar. */
    contaBancaria: boolean;
    contaDeRecebimento: ContaDeRecebimento;
    /** Cartão de crédito e boleto via intermediador: condição é o parcelamento da OPERADORA. */
    condicaoDaOperadora: boolean;
    /** Ícone do bloco, do protótipo. */
    icone: string;
}

/** O mínimo da forma que o perfil precisa saber — é o que vem no `select` do Finanças. */
export interface FormaParaPerfil {
    codigoPagamentoSefaz: string | null | undefined;
    idTipoDestino: number | null | undefined;
}

/**
 * A natureza do recebimento sai do CÓDIGO SEFAZ (tPag) e do TIPO DE DESTINO da forma cadastrada em
 * Finanças — nunca do nome. O protótipo mapeava por nome ("Dinheiro", "Boleto"); nomes são
 * editáveis pela empresa e traduziriam a regra para "quem renomeou a forma quebrou o pagamento".
 *
 * `meio` (Banco × Intermediador) é estado de UI: no dado gravado ele é derivável —
 * `idIntermediadorPagamento` preenchido é Intermediador, senão Banco.
 */
export function perfilDaForma(forma: FormaParaPerfil | null | undefined, meioIntermediador: boolean): PerfilDaForma {
    const base: PerfilDaForma = {
        categoria: true,
        condicao: false,
        grade: false,
        intermediacao: 'nenhuma',
        bandeira: false,
        contaBancaria: false,
        contaDeRecebimento: 'editavel',
        condicaoDaOperadora: false,
        icone: 'payments',
    };

    if (!forma) return base;

    switch (forma.codigoPagamentoSefaz) {
        case CodigoSefazEnum.Dinheiro:
            return { ...base, icone: 'payments' };

        case CodigoSefazEnum.Cheque:
            return { ...base, condicao: true, grade: true, icone: 'request_quote' };

        case CodigoSefazEnum.CartaoCredito:
            return {
                ...base,
                condicao: true,
                intermediacao: 'cartao',
                bandeira: true,
                contaDeRecebimento: 'leituraIntermediador',
                condicaoDaOperadora: true,
                icone: 'credit_card',
            };

        case CodigoSefazEnum.CartaoDebito:
            return {
                ...base,
                intermediacao: 'cartao',
                bandeira: true,
                contaDeRecebimento: 'leituraIntermediador',
                icone: 'credit_card',
            };

        case CodigoSefazEnum.Boleto:
            return {
                ...base,
                condicao: true,
                // Via intermediador a condição é a da operadora e não há grade; via banco é a da
                // empresa, com grade (§8.5, exceção do parcelamento da operadora).
                grade: !meioIntermediador,
                intermediacao: 'meio',
                contaDeRecebimento: meioIntermediador ? 'leituraIntermediador' : 'leituraBanco',
                condicaoDaOperadora: meioIntermediador,
                icone: 'receipt_long',
            };

        case CodigoSefazEnum.Pix:
            return {
                ...base,
                intermediacao: 'meio',
                contaDeRecebimento: meioIntermediador ? 'leituraIntermediador' : 'leituraBanco',
                icone: 'qr_code_2',
            };

        case CodigoSefazEnum.Deposito:
            return { ...base, contaBancaria: true, contaDeRecebimento: 'leituraBanco', icone: 'account_balance' };

        case CodigoSefazEnum.Crediario:
            return { ...base, condicao: true, grade: true, contaDeRecebimento: 'nenhuma', icone: 'calendar_month' };
    }

    // Formas cadastradas pela empresa (tPag 99 ou sem código): parceláveis, e a conta depende do
    // destino que a forma declara (§8.3 — "Caixa" e "Conta a Receber" são os dois casos da story).
    switch (forma.idTipoDestino) {
        case TipoDestinoEnum.Caixa:
            return { ...base, contaDeRecebimento: 'contaCaixa', icone: 'point_of_sale' };

        case TipoDestinoEnum.ContasAReceber:
            return { ...base, condicao: true, grade: true, contaDeRecebimento: 'obrigatoria', icone: 'account_balance_wallet' };
    }

    return { ...base, condicao: true, grade: true, icone: 'view_agenda' };
}

/** Texto da nota de mecanismo (§8.4) — uma linha, sem "taxa de juros". */
export function notaDoMecanismo(perfil: PerfilDaForma, taxaIntermediador: number | null): string | null {
    // Cartao de DEBITO: passa pelo intermediador, mas e a vista — falar em parcelamento aqui
    // estaria errado (achado da revisao em tela, 23/09).
    if (perfil.intermediacao === 'cartao' && !perfil.condicao) {
        return taxaIntermediador != null
            ? `Recebimento à vista pelo intermediador, com taxa de ${taxaIntermediador.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% descontada no recebimento.`
            : 'Recebimento à vista pelo intermediador; a taxa é descontada no recebimento.';
    }

    if (perfil.intermediacao === 'cartao' || perfil.condicaoDaOperadora) {
        return taxaIntermediador != null
            ? `Parcelamento definido pelo intermediador, com taxa de ${taxaIntermediador.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}% descontada no recebimento.`
            : 'Parcelamento definido pelo intermediador; a taxa é descontada no recebimento.';
    }

    if (!perfil.condicao) return 'Recebimento à vista, sem condição de pagamento nem parcelas.';

    return null;
}
